import React from 'react';
import {
	Dialog,
	DialogContent,
} from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { useGo } from '@/hooks/useResourceNavigation';

// Entity detail component registry
import { TariffDetails } from '@/pages/tariffs/tariff-details';
import { CustomerDetails } from '@/pages/customers/customer-details';
import { LimitDetails } from '@/pages/limits/limit-details';
import { ContractDetailsQuery } from '@/pages/contracts/contract-details-query';
import { PricingPolicyDetails } from '@/pages/pricing-policies/pricing-policy-details';
import { ScenarioDetails } from '@/pages/scenarios/scenario-details';
import { CampaignDetails } from '@/pages/campaigns/campaign-details';
import { VoucherDetails } from '@/pages/vouchers/voucher-details';
import { UserDetails } from '@/pages/users/user-details';
import { VehicleDetails } from '@/pages/vehicles/components/vehicle-details';

// Entity detail configuration interface
interface EntityDetailConfig {
	component: React.ComponentType<any>;
	idProp: string; // The prop name for the entity ID (e.g., 'tariffId', 'customerId')
	title: string; // Modal title
	resourcePath: string; // URL path for edit navigation (e.g., '/pricing-policies')
	supportsHeader?: boolean; // Whether the component supports showing header
}

// Registry mapping entity types to their detail components
const entityDetailsRegistry: Record<string, EntityDetailConfig> = {
	tariffs: {
		component: TariffDetails,
		idProp: 'tariffId',
		title: 'Tariff Details',
		resourcePath: '/tariffs',
		supportsHeader: true
	},
	customers: {
		component: CustomerDetails,
		idProp: 'customerId',
		title: 'Customer Details',
		resourcePath: '/customers',
		supportsHeader: true
	},
	limits: {
		component: LimitDetails,
		idProp: 'limitId',
		title: 'Limit Details',
		resourcePath: '/limits',
		supportsHeader: true
	},
	contracts: {
		component: ContractDetailsQuery,
		idProp: 'contractId',
		title: 'Contract Details',
		resourcePath: '/contracts'
	},
	'pricing-policies': {
		component: PricingPolicyDetails,
		idProp: 'pricingPolicyId',
		title: 'Pricing Policy Details',
		resourcePath: '/pricing-policies',
		supportsHeader: true
	},
	scenarios: {
		component: ScenarioDetails,
		idProp: 'scenarioId',
		title: 'Scenario Details',
		resourcePath: '/scenarios'
	},
	campaigns: {
		component: CampaignDetails,
		idProp: 'campaignId',
		title: 'Campaign Details',
		resourcePath: '/campaigns'
	},
	vouchers: {
		component: VoucherDetails,
		idProp: 'voucherId',
		title: 'Voucher Details',
		resourcePath: '/vouchers',
		supportsHeader: true
	},
	users: {
		component: UserDetails,
		idProp: 'userId',
		title: 'User Details',
		resourcePath: '/users'
	},
	vehicles: {
		component: VehicleDetails,
		idProp: 'vehicleId',
		title: 'Vehicle Details',
		resourcePath: '/vehicles'
	}
};

interface EntityDetailsModalProps {
	isOpen: boolean;
	onClose: () => void;
	entityType: string; // e.g., 'tariffs', 'customers'
	entityId: string;
	entityDisplayName?: string; // Optional: to show in modal title
}

export function EntityDetailsModal({
	isOpen,
	onClose,
	entityType,
	entityId,
}: EntityDetailsModalProps) {
	const go = useGo();
	const config = entityDetailsRegistry[entityType];

	if (!config) {
		console.warn(`No detail component registered for entity type: ${entityType}`);
		return null;
	}

	const { component: DetailComponent, idProp, resourcePath, supportsHeader } = config;

	// Navigation handlers
	const handleEdit = (id: string) => {
		onClose();
		go({ to: `${resourcePath}/edit/${id}` });
	};

	const handleClone = (id: string) => {
		onClose();
		go({ to: `${resourcePath}/clone/${id}` });
	};

	// Create props object with the correct ID prop name
	const detailProps: Record<string, unknown> = {
		[idProp]: entityId,
	};

	// Add header props if the component supports it
	if (supportsHeader) {
		detailProps.showHeader = true;
		detailProps.onEdit = handleEdit;
		detailProps.onClone = handleClone;
		detailProps.onClose = onClose;
		// Note: onDelete is intentionally omitted in modals for safety
	}

	return (
		<Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
			<DialogContent
				key={`${entityType}-${entityId}`}
				className="overflow-hidden flex flex-col bg-white dark:bg-slate-900 p-0 sm:p-0"
				size="lg"
				enablePreRender={true}
				preRenderTimeout={500}
				showCloseButton={false}
			>
				{/* Fixed-height scrollable content area - prevents resize on tab switch */}
				<div className="flex-1 overflow-y-auto min-h-0 h-[100dvh] sm:h-[75vh] p-5 sm:p-6">
					<React.Suspense
						fallback={
							<div className="flex items-center justify-center py-12">
								<Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
								<span className="text-sm text-muted-foreground">Loading details...</span>
							</div>
						}
					>
						<DetailComponent {...detailProps} />
					</React.Suspense>
				</div>
			</DialogContent>
		</Dialog>
	);
}

// Helper function to check if an entity type supports details
export function supportsEntityDetails(entityType: string): boolean {
	return entityType in entityDetailsRegistry;
}

// Helper function to get detail configuration for an entity type
export function getEntityDetailConfig(entityType: string): EntityDetailConfig | null {
	return entityDetailsRegistry[entityType] || null;
}
